import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, X, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const GREETING = {
    role: "assistant",
    content:
        "Hey there, I am Maya, your BlueLedger guide. Real quick, what brings you here today? Are you a freelancer, running a small team, or an accountant looking for a better tool? I can point you to the right things in under a minute.",
};

const SUGGESTIONS = [
    "How is this different from QuickBooks?",
    "What does the Free plan include?",
    "Tell me about Cayman no-tax mode",
    "Can I import from Zoho Books?",
];

const TEASER_TEXT = "Got a question? Chat with Maya.";

export default function SalesAgentWidget() {
    const [open, setOpen] = useState(false);
    const [teaserOpen, setTeaserOpen] = useState(false);
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    // Show a small teaser bubble after the visitor has been on the page a bit.
    useEffect(() => {
        const t = setTimeout(() => {
            if (!open) setTeaserOpen(true);
        }, 6000);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const openPanel = () => {
        setTeaserOpen(false);
        setOpen(true);
    };

    const sendText = async (text) => {
        const trimmed = (text || "").trim();
        if (!trimmed || busy) return;
        const nextMessages = [...messages, { role: "user", content: trimmed }];
        setMessages(nextMessages);
        setInput("");
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${BACKEND}/api/assistant/sales-chat`,
                {
                    messages: nextMessages
                        .filter((m) => m.role === "user" || m.role === "assistant")
                        .slice(-12),
                    session_id: sessionId,
                },
                { headers: { "Content-Type": "application/json" } },
            );
            setSessionId(data.session_id);
            setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "I hit a snag answering that. Mind trying again in a second? In the meantime, tap Start Free below and you'll be in your own BlueLedger workspace in under a minute.",
                },
            ]);
        } finally {
            setBusy(false);
        }
    };

    const send = () => sendText(input);

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const reset = () => {
        setMessages([GREETING]);
        setSessionId(null);
    };

    // Only show suggestion chips on the first turn.
    const showSuggestions = messages.length === 1 && !busy;

    return (
        <>
            {/* Teaser bubble */}
            <AnimatePresence>
                {teaserOpen && !open && (
                    <motion.button
                        initial={{ opacity: 0, y: 16, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        onClick={openPanel}
                        data-testid="sales-agent-teaser"
                        className="fixed z-40 bottom-24 right-6 max-w-[260px] text-left bg-white border border-sand-200 rounded-2xl shadow-card-hover p-4 pr-9 hover:-translate-y-0.5 transition-all"
                    >
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-secondary-dark font-semibold">
                            <Sparkles className="h-3 w-3" /> Maya · live
                        </div>
                        <div className="mt-1 text-sm text-ocean-700 leading-snug">{TEASER_TEXT}</div>
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                setTeaserOpen(false);
                            }}
                            className="absolute top-2 right-2 h-6 w-6 grid place-items-center rounded-full text-muted-foreground hover:bg-sand-100"
                            aria-label="Dismiss"
                        >
                            <X className="h-3.5 w-3.5" />
                        </span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Floating launcher */}
            <button
                onClick={() => (open ? setOpen(false) : openPanel())}
                data-testid="sales-agent-launcher"
                className={`fixed z-40 bottom-6 right-6 h-14 w-14 rounded-full shadow-card-hover grid place-items-center transition-all ${
                    open ? "bg-ocean-700 text-white" : "bg-deep-ocean text-white hover:scale-105"
                }`}
                aria-label="Open sales agent"
            >
                {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
                {!open && (
                    <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-coral ring-2 ring-sand-50 animate-pulse" />
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        data-testid="sales-agent-panel"
                        className="fixed z-40 bottom-24 right-6 w-[min(92vw,400px)] h-[600px] max-h-[calc(100vh-120px)] rounded-3xl bg-white border border-sand-200 shadow-card-hover overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="relative bg-deep-ocean text-white p-5">
                            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-secondary/30 blur-3xl" />
                            <div className="relative flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-11 w-11 rounded-full bg-gradient-to-br from-secondary to-coral grid place-items-center text-white font-heading text-lg ring-2 ring-white/20">
                                        M
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.2em] text-secondary-light font-semibold inline-flex items-center gap-1.5">
                                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> Online now
                                        </div>
                                        <div className="mt-0.5 font-heading text-lg leading-tight">Maya from BlueLedger</div>
                                        <div className="text-[11px] text-white/70">Your virtual product guide</div>
                                    </div>
                                </div>
                                <button
                                    onClick={reset}
                                    className="text-[11px] text-white/60 hover:text-white transition-colors"
                                    title="Start a new chat"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin bg-sand-50">
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`sales-msg-${m.role}-${i}`}
                                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "ml-auto bg-ocean-700 text-white rounded-br-sm"
                                            : "bg-white border border-sand-200 text-ocean-700 rounded-bl-sm shadow-soft"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            ))}
                            {busy && (
                                <div className="bg-white border border-sand-200 rounded-2xl rounded-bl-sm max-w-[85%] px-4 py-3 shadow-soft inline-flex items-center gap-2 text-muted-foreground text-sm">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Maya is typing
                                </div>
                            )}

                            {/* Suggestion chips */}
                            {showSuggestions && (
                                <div className="pt-2 space-y-2">
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                                        Try asking
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {SUGGESTIONS.map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => sendText(s)}
                                                data-testid={`sales-suggestion-${s.slice(0, 10).replace(/\W/g, "")}`}
                                                className="text-[12px] text-ocean-700 border border-sand-300 bg-white rounded-full px-3 py-1.5 hover:border-secondary hover:text-secondary-dark transition-colors"
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Persistent CTA strip */}
                        <div className="px-3 pt-3 bg-white border-t border-sand-200">
                            <div className="grid grid-cols-2 gap-2">
                                <Link to="/pricing" onClick={() => setOpen(false)} className="block">
                                    <Button
                                        variant="outline"
                                        data-testid="sales-cta-pricing"
                                        className="w-full h-10 rounded-xl border-sand-300 text-ocean-700 hover:bg-sand-50"
                                    >
                                        See pricing
                                    </Button>
                                </Link>
                                <Link to="/register" onClick={() => setOpen(false)} className="block">
                                    <Button
                                        data-testid="sales-cta-signup"
                                        className="w-full h-10 rounded-xl bg-coral hover:bg-coral-dark text-white"
                                    >
                                        Start free <ArrowRight className="h-4 w-4 ml-1.5" />
                                    </Button>
                                </Link>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-1.5 text-center">
                                Free forever plan · No credit card required
                            </div>
                        </div>

                        {/* Composer */}
                        <div className="border-t border-sand-200 p-3 bg-white">
                            <div className="flex gap-2">
                                <Input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    placeholder="Ask Maya anything about BlueLedger"
                                    disabled={busy}
                                    className="h-11 rounded-xl border-sand-300"
                                    data-testid="sales-agent-input"
                                />
                                <Button
                                    onClick={send}
                                    disabled={busy || !input.trim()}
                                    className="h-11 w-11 p-0 bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl"
                                    data-testid="sales-agent-send"
                                >
                                    <Send className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
